import { ReactElement, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-material.css'
import { ColumnSettings } from './ColumnSettings'
import { useAllColumnsQuery, useUserPrefsColumnsQuery, useAllQueuedContacts, useMetadataFieldsQuery, useUpdateContactQueuePreference } from '../api'
import { convertToColumnDataJson, mapColumns } from '../utils'
import { CategoryColumns, MetaDataType, UserPrefsColumn } from '../constants'
import { COLUMN_CATEGORY, COLUMN_I18N_MAPPING } from '../../Recordings/recordings.constants'
import { Route, useRouteMatch, useHistory } from 'react-router-dom'
import MediaPlayer from '../../Recordings/MediaPlayer'
import { SCOPE_TYPE } from '../../Recordings/MediaPlayer/mediaPlayer.constants'
import { trimEnd } from 'lodash'
import { OrganizationProvider } from '../../../Common/Organization/organization-context'
import queryString from 'query-string'
import { PERMISSIONS, usePermission } from '../../../Permissions'
import { DragStoppedEvent } from 'ag-grid-community'
import { ComboBox, ComboBoxItem } from '@calabriocloud/beacon-ui'

interface GoalNameComboBoxItem {
  id: string,
  textValue: string,
}

function mapCategoryToColumns(columns: CategoryColumns[]): UserPrefsColumn[] {
  return columns.flatMap(item => item.columns.map(
    col => ({
      ...col,
      id: `${item.category}_${col.key}`,
      category: item.category,
      isSelected: col.isSelected && col.isEnabled,
      label: item.category === COLUMN_CATEGORY.METADATA ? col.name : COLUMN_I18N_MAPPING[col.key] || col.key,
      width: col.width ? col.width : 200,
      disablePadding: false,
    }),
  ))
}

function reduceMetadataToCategory(metadata: MetaDataType[]) {
  return metadata.reduce<CategoryColumns>(
    (acc: CategoryColumns, field, index) => {
      acc.columns.push({
        // id will be set later. It will be `${category}_${key}`
        id: '',
        isEnabled: true,
        isSelected: false,
        key: field.key,
        name: field.name,
        label: field.name,
        order: 10000 + index,
        width: 0,
      })
      return acc
    },
    { category: COLUMN_CATEGORY.METADATA, columns: [] },
  )
}

export const ContactQueueGrid = (): ReactElement => {
  const { t } = useTranslation()
  const match = useRouteMatch()
  const history = useHistory()
  const { data: defaultColumns, isLoading: isLoadingAllColumn } = useAllColumnsQuery()
  const { data: userPrefsColumns, isLoading: isLoadingAllUserPrefs } = useUserPrefsColumnsQuery()
  const { data: metadataFields = [], isLoading: isLoadingMetaFields } = useMetadataFieldsQuery()
  const { data: allQueuedContacts, refetch: refetchQueuedContacts, isFetching: isFetchingQueuedContacts } = useAllQueuedContacts()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [placeHolder, setPlaceHolder] = useState<string>('filterGoalName')
  const [updateContactQueuePreference] = useUpdateContactQueuePreference()
  const gridRef = useRef<AgGridReact>(null)
  const hasViewCustomMetadataPermission = usePermission(PERMISSIONS.VIEW_CUSTOM_METADATA)
  const closeContact = () => {
    // Refresh the queue data when closing a contact
    refetchQueuedContacts()
    const queryParams = queryString.parse(location.search)
    // remove subApp and goalId query params when closing contact opened by contact queue
    if (queryParams.subApp && queryParams.subApp === 'contactQueue') {
      delete queryParams.subApp
      delete queryParams.goalId
    }
    history.push(`${match.url}${queryString.stringify(queryParams)}`)
  }
  const openContact = (ccrid: number | string, scopeType?: string, locationParam?: { search: string }, goalId?: number) => {
    const goalPath = goalId ? `?subApp=contactQueue&goalId=${goalId}` : ''
    history.push(`${trimEnd(match.url, '/')}/${ccrid}${scopeType ? `/${scopeType}${goalPath}` : ''}${locationParam?.search || ''}`) // using lodash's trimEnd() function because when navigating to a recording from a classic page, an extra '/' can be appended in the URL. See (SAAS-64046)
  }

  const columns = useMemo(() => {
    // when api loading, return empty array to avoid rendering the grid
    if (isLoadingAllUserPrefs || isLoadingMetaFields || isLoadingAllColumn) {
      return []
    }

    const permittedMetadata: CategoryColumns = hasViewCustomMetadataPermission ? reduceMetadataToCategory(metadataFields) : { category: COLUMN_CATEGORY.METADATA, columns: [] }
    let baseColumns = mapCategoryToColumns([...(defaultColumns || []), permittedMetadata]).map(col => ({ ...col }))

    if (userPrefsColumns?.length) {
      const userPrefsColumnsMap = userPrefsColumns.flatMap(item => item.columns.map(col => ({ ...col }))) || []
      baseColumns = baseColumns.map(baseCol => {
        const userCol = userPrefsColumnsMap.find(userCol => userCol.id === baseCol.id)
        if (userCol) {
          return {
            ...baseCol,
            ...userCol, // Spread userCol to override baseCol properties
          }
        }
        return baseCol
      })
    }

    return baseColumns.sort((a, b) => a.order - b.order)

  }, [defaultColumns, userPrefsColumns, metadataFields, isLoadingAllUserPrefs, isLoadingMetaFields, isLoadingAllColumn])

  const columnDefs = useMemo(() => {
    const i18nifiedColumnData = columns
      ?.filter(item => item.isSelected)
      .map(column => ({
        field: column.id.toString(),
        headerName: t(column.label),
        width: column.width,
      }))
    return i18nifiedColumnData
  }, [columns, t])

  const rowData = useMemo(() => {
    if (isFetchingQueuedContacts) {
      // Return undefined to show loading state in the grid
      return undefined
    }
    return allQueuedContacts?.map(contact => {
      // mapColumns figures out the key for the row and determines if the value needs to be translated and the translatlion key
      const row = mapColumns(columns, contact)
      return Object.entries(row).reduce((acc: Record<string, unknown>, [key, val]) => {
        if (typeof val === 'object' && val !== null && 'value' in val && 'translate' in val) {
          if (typeof val.value === 'string' && val.translate === true)
            acc[key] = t(val.value)
          else
            acc[key] = val.value
        }
        return acc
      }, {})
    })
  }, [columns, allQueuedContacts, isFetchingQueuedContacts, t])

  // unique goal_fields_goalName values for the ComboBox
  const uniqueGoalName: GoalNameComboBoxItem[] = useMemo(() => {
    if (!rowData) return []
    const goalNames = new Set(rowData?.map(row => row.goal_fields_goalName))
    return Array.from(goalNames).map(name => {
      const row = rowData?.find(row => row.goal_fields_goalName === name)
      return { id: String(row?.contact_fields_id), textValue: String(row?.goal_fields_goalName) }
    })
  }, [rowData])

  // Filtered rowData based on selectedKey
  const filteredRowData = useMemo(() => {
    if (!selectedKey || !rowData) return rowData
    const goal = rowData.find(row => String(row.contact_fields_id) === selectedKey)
    return rowData.filter(row => row.goal_fields_goalName === goal?.goal_fields_goalName)
  }, [rowData, selectedKey])

  // Handling the focus and blur events for the ComboBox to manage placeholder text
  const handleFocus = () => {
    if (placeHolder === 'filterGoalName') {
      setPlaceHolder('')
    }
  }
  const handleBlur = () => {
    if (!placeHolder || !selectedKey) {
      setPlaceHolder('filterGoalName')
    }
  }

  // Handling the drag stopped event to update the column order in user preferences
  const handleDragStopped = (event?: DragStoppedEvent) => {

    const displayedColumnsCenter = event?.columnApi?.getDisplayedCenterColumns?.() || []

    const newColumns = columns.map(col => {
      const updatedColIndex = displayedColumnsCenter.findIndex(x => x.getColDef().field === col.id)
      const order = updatedColIndex > -1 ? updatedColIndex : col.order
      return { ...col, order }
    })

    updateContactQueuePreference(convertToColumnDataJson(newColumns))
  }

  // Handling the different states that trigger the overlays in the grid
  if (isFetchingQueuedContacts) {
    gridRef.current?.api?.showLoadingOverlay()
  } else if (!rowData?.length) {
    gridRef.current?.api?.showNoRowsOverlay()
  } else {
    gridRef.current?.api?.hideOverlay()
  }

  return (
    <>
      <Route
        path={`${match.url}/:ccrid/:scopeType`}
        render={({ match, location }) => (
          <div style={{
            position: 'fixed',
            top: '48px',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
          }}>
            <OrganizationProvider>
              <MediaPlayer
                ccrid={parseInt(match.params.ccrid)}
                onClose={closeContact}
                openContact={
                  (ccrid: number) =>
                    openContact(
                      ccrid,
                      SCOPE_TYPE.CCR,
                      location,
                    ) /*When opening contact in media player (only used for associated contacts) always will be a CCR */
                }
                isExpanded={true} //true
                scopeType={match.params.scopeType}
                goalId={new URLSearchParams(location.search).get('goalId') ? parseInt(new URLSearchParams(location.search).get('goalId') || '') || undefined : undefined}
                showExpandButton={false}
                data-testid="contact-queue-grid-media-player"
              />
            </OrganizationProvider>
          </div>
        )
        }
      />
      <div className="flex flex-col gap-2">
        <div className="flex justify-end gap-6">
          <ComboBox
            data-testid={'goal-select'}
            isDisabled={ isLoadingAllUserPrefs || isLoadingMetaFields || isLoadingAllColumn || isFetchingQueuedContacts }
            className="w-60 gap-0"
            allowsCustomValue
            inputValue={t(placeHolder)}
            onInputChange={setPlaceHolder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            items={uniqueGoalName}
            onSelectionChange={ key => {
              if (!key) {
                setSelectedKey(null)
              } else {
                setSelectedKey(String(key))
              }
            }}
          >
            {item => (
              <ComboBoxItem id={item.id} key={item.textValue}>
                {item.textValue}
              </ComboBoxItem>
            )}
          </ComboBox>
          <ColumnSettings columns={columns} data-testid="contact-queuegrid-column-settings-component" />
        </div>
        <div className="ag-theme-material w-full h-[60vh] text-sm text-gray-700" style={{ borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <AgGridReact
            ref={gridRef}
            rowData={filteredRowData}
            columnDefs={columnDefs}
            defaultColDef={{
              flex: 1,
              minWidth: 150,
              resizable: true,
              sortable: true,
              filter: true,
              lockPinned: true,
              suppressMenu: true, // Disable the header menu
              headerClass: 'text-sm text-gray-700 font-semibold bg-gray-50',
            }}
            pagination={true}
            paginationPageSize={25}
            animateRows={false}
            onDragStopped={handleDragStopped} // reordering
            // TODO: Use a spinner component for loading state
            loadingOverlayComponent={() => <div className="ag-overlay-loading-center" data-testid="contact-queue-grid-loading-overlay"> {t('loading')} </div>}
            noRowsOverlayComponent={() => <div className="ag-overlay-no-rows-center" data-testid="contact-queue-grid-no-rows-overlay">{t('contactQueueInteractionsNoGoalToShow')}</div>}
            onRowDoubleClicked={event => {
              const hasValidContactId =
                event.data?.contact_fields_id !== undefined &&
                typeof event.data.contact_fields_id === 'number'

              const hasValidOrEmptyGoalId =
                event.data?.goal_fields_goalId === undefined ||
                typeof event.data.goal_fields_goalId === 'number'
              // Check if event has data and contains the necessary identifier(s)
              if (hasValidContactId && hasValidOrEmptyGoalId) {
                openContact(
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Verified it is a number and not undefined with hasValidContactId
                  event.data?.contact_fields_id as number,
                  SCOPE_TYPE.CCR,
                  { search: location.search },
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Verified it is a number or undefined with hasValidOrEmptyGoalId
                  event.data?.goal_fields_goalId as number | undefined,
                )
              }
            }}
            data-testid="contact-queue-grid-aggrid"
          />
        </div>
      </div>
    </>
  )
}
